import { useState } from "react";
import { X } from "lucide-react";
import { IEventApcap } from "../@types/system";

interface EventGalleryDisplayProps {
  data: IEventApcap;
}

interface EventPhoto {
  id: string;
  url: string;
  filename: string;
}

export function EventGalleryDisplay({ data }: EventGalleryDisplayProps) {
  const [selectedPhoto, setSelectedPhoto] = useState<EventPhoto | null>(null);

  if (data.gallery && data.gallery.length === 0) {
    return (
      <div className="grid gap-3 lg:grid-cols-form">
        <label className="text-sm font-medium text-zinc-700 dark:text-zinc-100">
          Galeria de fotos
        </label>
        <div className="flex items-center justify-center p-8 border border-zinc-200 dark:border-zinc-700 rounded-lg">
          <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-2 text-sm text-zinc-500 dark:text-zinc-400">
            Carregando fotos...
          </span>
        </div>
      </div>
    );
  }

  if (!data.gallery || data.gallery.length === 0) {  
    return (
      <div className="grid gap-3 lg:grid-cols-form">
        <label className="text-sm font-medium text-zinc-700 dark:text-zinc-100">
          Galeria de fotos
        </label>
        <div className="flex items-center justify-center p-8 border border-zinc-200 dark:border-zinc-700 rounded-lg bg-zinc-50 dark:bg-zinc-800">
          <span className="text-sm text-zinc-500 dark:text-zinc-400">
            Nenhuma foto disponível para este evento
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="grid gap-3 lg:grid-cols-form">
        <label className="text-sm font-medium text-zinc-700 dark:text-zinc-100">
          Galeria de fotos
        </label>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {data.gallery && data.gallery.map((photo: EventPhoto) => (
              <div
                key={photo.id}
                className="relative group aspect-square rounded-lg overflow-hidden bg-zinc-100 dark:bg-zinc-800 cursor-pointer transition-transform duration-200 hover:scale-105"
                onClick={() => setSelectedPhoto(photo)}
              >
                <img
                  src={photo.url}
                  alt={`Foto do evento ${data.name}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                
                {/* Overlay no hover */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                  <span className="text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    Clique para ampliar
                  </span>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            {data.gallery && data.gallery.length} {data.gallery && data.gallery.length === 1 ? 'foto' : 'fotos'} encontrada{data.gallery && data.gallery.length === 1 ? '' : 's'}
          </p>
        </div>
      </div>

      {/* Modal de foto ampliada */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            <img
              src={selectedPhoto.url}
              alt={`Foto do evento ${data.name}`}
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </>
  );
} 